import { useNavigate } from 'react-router-dom';
import {
  Box,
  Skeleton,
  Theme,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ACTIVITY_LIBRARY_ROUTE, SIGNUP_ROUTE } from '../../utils/routes';
import {
  setActivityLibraryBlock,
  clearFolder,
} from '../../redux/activityLibrary/activityLibrarySlice';
import { logout } from '../../redux/GlobalAction';

interface INavbarMiniCellProps {
  title: string;
  url: string;
  src: string;
  isSelected: boolean;
  boldIcon: string;
  hidden: boolean;
  setHidden: React.Dispatch<React.SetStateAction<boolean>>;
}

const StyledBox = styled(Box)(({ theme, isSelected }: { theme: Theme; isSelected: boolean }) => ({
  cursor: 'pointer',
  '.navbarLink': {
    padding: '14px 0 14px 0',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isSelected ? 'rgba(124, 174, 190, 0.2)' : 'transparent',
    borderLeft: isSelected ? 'rgb(93,171,201) 3px solid' : 'none',
    borderRadius: '2px',
    fontWeight: isSelected ? theme.typography.bodyBold : theme.typography.bodyRegular,
    '&:hover': {
      backgroundColor: 'rgb(93,171,201,0.1)',
      color: theme.palette.primary.dark,
      transition: 'all 0.2s ease-in-out',
    },
  },
}));

const NavbarMiniCell = ({
  isSelected,
  url,
  src,
  title,
  boldIcon,
  hidden,
  setHidden,
}: INavbarMiniCellProps) => {
  const { t } = useTranslation();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const handleOnClick = () => {
    if (!url) {
      setHidden(!hidden);
      return;
    }
    setHidden(true);
    switch (url) {
      case SIGNUP_ROUTE:
        dispatch(logout());
        break;
      case ACTIVITY_LIBRARY_ROUTE:
        dispatch(setActivityLibraryBlock());
        dispatch(clearFolder());
        navigate(url);
        break;
      default:
        navigate(url);
        break;
    }
  };

  return (
    <StyledBox
      isSelected={isSelected}
      theme={theme}
      onClick={handleOnClick}
      minWidth={isSmallScreen ? '80px' : '84px'}
    >
      <Box className="navbarLink">
        {isImageLoading && <Skeleton variant="rectangular" width="15px" height="15px" />}
        {url === '' ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <img
              src={boldIcon}
              alt="logo"
              style={{
                display: isImageLoading ? 'none' : 'block',
                width: '25px',
                height: '25px',
              }}
              onLoad={() => setIsImageLoading(false)}
            />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <img
              src={isSelected ? boldIcon : src}
              alt="logo"
              style={{
                minWidth: '30px',
                display: isImageLoading ? 'none' : 'flex',
                width: '25px',
                height: '25px',
              }}
              onLoad={() => setIsImageLoading(false)}
            />
          </Box>
        )}
        <Box display="flex" flexDirection="column">
          <Tooltip title={t(title)}>
            <Typography
              variant={isSelected ? 'bodyBold' : 'bodyRegular'}
              color="primary"
              fontSize={isSmallScreen ? '10px' : '11px'}
              textAlign="left"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              maxWidth="70px"
            >
              {t(title)}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </StyledBox>
  );
};

export default NavbarMiniCell;
